import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';

import { catchError, tap, throwError } from 'rxjs';

import { isTokenRequest } from '../../shared/utils/is-token-request.util';
import {
  pickSafeHeaders,
  redactSensitive,
  redactUrl,
} from '../../shared/utils/redact-sensitive.util';
import { NotificationsService } from '../services/ui/notifications.service';
import { SKIP_ACTIVITY_LOG } from './skip-activity-log.token';

/**
 * Functional HTTP interceptor that captures every server response and pushes it
 * to NotificationsService so it appears in the notifications drawer.
 *
 * Token-fetch calls (e.g. the Nick Fury backend's firebase-token call) are excluded —
 * same rule as request-log.interceptor.ts, so internal auth plumbing doesn't clutter the
 * drawer either.
 */
export function notificationsInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const notifs = inject(NotificationsService);
  const startedAt = performance.now();
  const shouldNotify =
    req.method !== 'GET' && !isTokenRequest(req.urlWithParams) && !req.context.get(SKIP_ACTIVITY_LOG);

  return next(req).pipe(
    tap((event) => {
      if (shouldNotify && event instanceof HttpResponse) {
        notifs.push({
          statusCode: event.status,
          statusText: event.statusText,
          url: redactUrl(event.url ?? req.urlWithParams),
          method: req.method,
          data: redactSensitive(event.body),
          requestPayload: redactSensitive(req.body),
          headers: pickSafeHeaders(headersToRecord(event.headers)),
          responseTimeMs: Math.round(performance.now() - startedAt),
        });
      }
    }),
    catchError((error: unknown) => {
      if (shouldNotify && error instanceof HttpErrorResponse) {
        notifs.push({
          statusCode: error.status,
          statusText: error.statusText,
          url: redactUrl(error.url ?? req.urlWithParams),
          method: req.method,
          data: redactSensitive(error.error),
          requestPayload: redactSensitive(req.body),
          headers: pickSafeHeaders(headersToRecord(error.headers)),
          responseTimeMs: Math.round(performance.now() - startedAt),
        });
      }

      return throwError(() => error);
    }),
  );
}

function headersToRecord(headers: HttpHeaders): Record<string, string> {
  return headers.keys().reduce<Record<string, string>>((acc, key) => {
    acc[key] = headers.getAll(key)?.join(', ') ?? '';
    return acc;
  }, {});
}
